import { ActiveCorners } from "../components/process-design/corner/CornerConstants";
import { rangeSelection, helper } from "./Cells";
import { sortRange, splitCellId, rangeIndexToLabel, rangeLabelToIndex } from "../components/process-design/cell/Utils";
import { unifyRanges, isCellWithinRange, cellIndexToLabel } from "../utils/util";
import { stateHistory } from "./state-history";
import { UndoRedoTypes } from "../utils/constants";

export default {
  namespaced: true,
  state: {
    ctrlPressed: false,
  },
  actions: {
    setCtrlPressed({ state }, isPressed) {
      state.ctrlPressed = isPressed;
    },
    setNextActiveRowCell({ dispatch, getters, rootState, rootGetters }, delta) {
      const [columnIndex, rowIndex] = rootGetters["cells/getCellIndexById"](rootState.cells.activeCellId);
      const nextRowIndex = getters.getNextActiveRowCell(columnIndex, rowIndex, delta);

      const id = rootGetters["cells/getCellIdByIndex"]([columnIndex, nextRowIndex]);
      if (!id) return;

      const label = rootState.cells.mergedCellIds?.[id]
        ? rangeIndexToLabel(rootGetters["cells/getRangeByCellId"](id))
        : cellIndexToLabel(columnIndex, nextRowIndex);

      dispatch("cells/setActiveRangeMainCellCoordinate", rangeLabelToIndex(label), { root: true });
      dispatch("updateMovement", { rangeLabel: label, activeCellId: id });
    },
    setNextActiveColumnCell({ dispatch, getters, rootState, rootGetters }, delta) {
      const [columnIndex, rowIndex] = rootGetters["cells/getCellIndexById"](rootState.cells.activeCellId);
      const nextColumnIndex = getters.getNextActiveColumnCell(columnIndex, rowIndex, delta);

      const id = rootGetters["cells/getCellIdByIndex"]([nextColumnIndex, rowIndex]);
      if (!id) return;

      const label = rootState.cells.mergedCellIds?.[id]
        ? rangeIndexToLabel(rootGetters["cells/getRangeByCellId"](id))
        : cellIndexToLabel(nextColumnIndex, rowIndex);

      dispatch("cells/setActiveRangeMainCellCoordinate", rangeLabelToIndex(label), { root: true });
      dispatch("updateMovement", { rangeLabel: label, activeCellId: id });
    },
    async updateMovement({ dispatch, rootState, rootGetters }, { rangeLabel, activeCellId }) {
      const range = rangeLabel.split(":");
      const newRange = range[0] === range[1] ? range[0] : rangeLabel;

      const inFormulaMode =
        rootState.cells.editCell.formulaRangeSelectionAllowed && rootState.cells.editCell.text.trim().startsWith("=");

      if (inFormulaMode) {
        // inFormulaMode Normal flow (NO undo/redo)
        if (!rootState.cells.editCell.range) {
          dispatch("cells/addFormulaRange", newRange, { root: true });
        } else {
          const activeRangeId = rootState.cells.rangeDragging.id || rangeSelection.activeRangeId;
          dispatch("cells/updateActiveRange", { id: activeRangeId, title: newRange }, { root: true });
          dispatch("cells/setEditCell", { range: newRange }, { root: true });
        }
        return;
      }
      const mainRange = rootGetters["cells/mainRange"];

      // --- Undo/Redo Flow for Non-formula mode ---
      const prevFormulaRange = rangeIndexToLabel(mainRange.range);
      const prevActiveCellId = rootState.cells.activeCellId;
      let reverseArr = [];

      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(
            await helper.range.main.setRange({
              prevActiveCellId,
              prevFormulaRange,
              activeCellId,
              newRange,
            })
          );
        },
      };

      await stateHistory.snapshot(undoObj, UndoRedoTypes.RangeSelection);
    },
    async calculateRange({ dispatch, state, rootGetters }, range) {
      if (range.length == 2) range = [...range, ...range];
      const visitedCells = {};
      let rangeIds = [];

      do {
        rangeIds = rootGetters["cells/rangeIds"](range).filter((item) => !visitedCells[item]);
        rangeIds.forEach((cellId) => {
          visitedCells[cellId] = true;
          const cellRange = rootGetters["cells/getRangeByCellId"](cellId);
          range = unifyRanges(range, cellRange);
        });
      } while (rangeIds.length > 0);

      return sortRange(range);
    },
    async increaseRight({ dispatch, getters, rootState }, range) {
      if (range[2] + 1 >= rootState.cells.current?.design.columns.length) return range;

      const endColIdx = getters.ctrlPressed
        ? getters.getNextActiveColumnCell(range[2], rootState.cells.activeRangeMainCellCoordinate[1], 1)
        : getters.getNextHorizontalCell(range[2], 1);

      return unifyRanges(range, await dispatch("calculateRange", [endColIdx, range[1], endColIdx, range[3]]));
    },
    async decreaseRight({ dispatch, getters, rootState }, range) {
      const newRange = await dispatch("calculateRange", [range[2], range[1], range[2], range[3]]);

      if (isCellWithinRange(rootState.cells.activeRangeMainCellCoordinate, newRange)) {
        return await dispatch("increaseLeft", range);
      }

      const endColIdx = getters.ctrlPressed
        ? getters.getNextActiveColumnCell(range[2], rootState.cells.activeRangeMainCellCoordinate[1], -1)
        : getters.getNextHorizontalCell(range[2], -1);

      return [range[0], range[1], endColIdx, range[3]];
    },
    async increaseLeft({ dispatch, getters, rootState }, range) {
      if (range[0] - 1 < 0) return range;

      const startColIdx = getters.ctrlPressed
        ? getters.getNextActiveColumnCell(range[0], rootState.cells.activeRangeMainCellCoordinate[1], -1)
        : getters.getNextHorizontalCell(range[0], -1);
      return unifyRanges(range, await dispatch("calculateRange", [startColIdx, range[1], startColIdx, range[3]]));
    },
    async decreaseLeft({ dispatch, getters, rootState }, range) {
      const newRange = await dispatch("calculateRange", [range[0], range[1], range[0], range[3]]);

      if (isCellWithinRange(rootState.cells.activeRangeMainCellCoordinate, newRange)) {
        return await dispatch("increaseRight", range);
      }

      const startColIdx = getters.ctrlPressed
        ? getters.getNextActiveColumnCell(range[0], rootState.cells.activeRangeMainCellCoordinate[1], 1)
        : getters.getNextHorizontalCell(range[0], 1);

      const sortedRange = sortRange([startColIdx, range[1], range[2], range[3]]);
      // TO_ASK: Why this is required
      sortedRange[0] = Math.min(sortedRange[0], rootState.cells.activeRangeMainCellCoordinate[0]);
      return sortedRange;
    },
    async increaseTop({ dispatch, getters, rootState }, range) {
      if (range[1] < 1) return range;

      const startRowIdx = getters.ctrlPressed
        ? getters.getNextActiveRowCell(rootState.cells.activeRangeMainCellCoordinate[0], range[1], -1)
        : getters.getNextVerticalCell(range[1], -1);

      return unifyRanges(range, await dispatch("calculateRange", [range[0], startRowIdx, range[2], startRowIdx]));
    },
    async decreaseTop({ dispatch, getters, rootState }, range) {
      const newRange = await dispatch("calculateRange", [range[0], range[1], range[2], range[1]]);

      if (isCellWithinRange(rootState.cells.activeRangeMainCellCoordinate, newRange)) {
        return await dispatch("increaseBottom", range);
      }

      const startRowIdx = getters.ctrlPressed
        ? getters.getNextActiveRowCell(rootState.cells.activeRangeMainCellCoordinate[0], range[1], 1)
        : getters.getNextVerticalCell(range[1], 1);

      const sortedRange = sortRange([range[0], startRowIdx, range[2], range[3]]);
      sortedRange[1] = Math.min(sortedRange[1], rootState.cells.activeRangeMainCellCoordinate[1]);
      return sortedRange;
    },
    async increaseBottom({ dispatch, getters, rootState }, range) {
      if (range[3] + 1 >= rootState.cells.current?.design.rows.length) return range;

      const endRowIdx = getters.ctrlPressed
        ? getters.getNextActiveRowCell(rootState.cells.activeRangeMainCellCoordinate[0], range[3], 1)
        : getters.getNextVerticalCell(range[3], 1);

      return unifyRanges(range, await dispatch("calculateRange", [range[0], endRowIdx, range[2], endRowIdx]));
    },
    async decreaseBottom({ dispatch, getters, rootState }, range) {
      const newRange = await dispatch("calculateRange", [range[0], range[3], range[2], range[3]]);

      if (isCellWithinRange(rootState.cells.activeRangeMainCellCoordinate, newRange)) {
        return await dispatch("increaseTop", range);
      }

      const endRowIdx = getters.ctrlPressed
        ? getters.getNextActiveRowCell(rootState.cells.activeRangeMainCellCoordinate[0], range[3], -1)
        : getters.getNextVerticalCell(range[3], -1);

      return [range[0], range[1], range[2], endRowIdx];
    },
    async moveRangeUp({ dispatch, getters, rootState }, range) {
      if (range[1] < 1) return range;
      const startRowIdx = getters.getNextVerticalCell(range[1], -1);
      const endRowIdx = getters.getNextVerticalCell(range[3], -1);
      return await dispatch("calculateRange", [range[0], startRowIdx, range[2], endRowIdx]);
    },
    async moveRangeDown({ dispatch, getters, rootState }, range) {
      if (range[3] + 1 >= rootState.cells.current?.design.rows.length) return range;
      const startRowIdx = getters.getNextVerticalCell(range[1], 1);
      const endRowIdx = getters.getNextVerticalCell(range[3], 1);
      return await dispatch("calculateRange", [range[0], startRowIdx, range[2], endRowIdx]);
    },
    async moveRangeLeft({ dispatch, getters, rootState }, { range, shiftCount }) {
      if (range[0] - 1 < 0) return range;
      const endColIdx = getters.getNextHorizontalCell(range[2], -1 * shiftCount);
      const startColIdx = getters.getNextHorizontalCell(range[0], -1 * shiftCount);
      return await dispatch("calculateRange", [startColIdx, range[1], endColIdx, range[3]]);
    },
    async moveRangeRight({ dispatch, getters, rootState }, { range, shiftCount }) {
      if (range[2] + 1 >= rootState.cells.current?.design.columns.length) return range;
      const endColIdx = getters.getNextHorizontalCell(range[2], shiftCount);
      const startColIdx = getters.getNextHorizontalCell(range[0], shiftCount);
      return await dispatch("calculateRange", [startColIdx, range[1], endColIdx, range[3]]);
    },

    // Adding undo/redo support for range operations
    async moveRangeSelection({ dispatch, getters, rootState, rootGetters }, { direction, shiftCount = 1 }) {
      const activeRangeId = rootState.cells.rangeDragging?.id || rangeSelection.activeRangeId;
      let oldRange = getters.getRangeById(activeRangeId);
      let newRange;

      switch (direction) {
        case "up":
          newRange = await dispatch("moveRangeUp", oldRange);
          break;
        case "down":
          newRange = await dispatch("moveRangeDown", oldRange);
          break;
        case "right":
          newRange = await dispatch("moveRangeRight", { range: oldRange, shiftCount });
          break;
        default:
          newRange = await dispatch("moveRangeLeft", { range: oldRange, shiftCount });
      }
      const rangeLabel = rangeIndexToLabel(newRange);
      let isFormulaMode = false;

      if (
        (rootState.cells.editCell.edited && rootState.cells.editCell.text.startsWith("=")) ||
        rootState.cells.rangeDragging.id
      ) {
        dispatch("cells/updateActiveRange", { id: activeRangeId, title: rangeLabel }, { root: true });
        dispatch("cells/setEditCell", { range: rangeLabel }, { root: true });
        isFormulaMode = true;
      }
      // Create undo/redo action
      let reverseArr = [];
      const undoObj = {
        undo: async () => {
          for (const func of reverseArr) {
            await func();
          }
        },
        redo: async () => {
          reverseArr = [];
          reverseArr.push(await helper.range.main.moveRange({ oldRange, newRange, isFormulaMode }));
        },
      };

      await stateHistory.snapshot(undoObj, UndoRedoTypes.RangeSelection);
    },
    updateShiftMovement({ dispatch, rootState }, { rangeLabel, activeRangeId }) {
      if (
        (rootState.cells.editCell.edited && rootState.cells.editCell.text.startsWith("=")) ||
        rootState.cells.rangeDragging.id
      ) {
        if (rootState.cells.editCell.formulaRangeSelectionAllowed && !rootState.cells.editCell.range) {
          dispatch("cells/addFormulaRange", rangeLabel, { root: true });
        } else {
          const mode = rootState.cells.rangeDragging?.drag ? "fill" : undefined;

          dispatch("cells/updateActiveRange", { id: activeRangeId, title: rangeLabel, mode }, { root: true });
          dispatch("cells/setEditCell", { range: rangeLabel }, { root: true });
        }
      } else {
        dispatch("cells/setFormulaRange", { title: rangeLabel }, { root: true });
      }
    },
  },

  getters: {
    ctrlPressed: (state) => state.ctrlPressed,
    getRangeById: (state, getters, rootState, rootGetters) => (rangeId) => {
      const range = rootGetters["cells/activeRange"](rangeId).range;
      return sortRange(range);
    },
    getNextActiveRowCell: (state, getters, rootState, rootGetters) => (columnIndex, rowIndex, delta) => {
      const lastVisibleRowIndex = getters.lastVisibleRowIndex;
      const firstVisibleRowIndex = getters.firstVisibleRowIndex;

      if ((delta === 1 && rowIndex >= lastVisibleRowIndex) || (delta === -1 && rowIndex <= firstVisibleRowIndex)) {
        return delta === 1 ? lastVisibleRowIndex : firstVisibleRowIndex;
      }

      const newRowIndex =
        !getters.hasData(rowIndex, columnIndex) || !getters.hasData(rowIndex + delta, columnIndex)
          ? getters.nextRowIndexWithData(columnIndex, rowIndex + delta, delta)
          : getters.prevRowIndexWithData(columnIndex, rowIndex + delta, delta);

      return newRowIndex;
    },
    getNextActiveColumnCell: (state, getters, rootState, rootGetters) => (columnIndex, rowIndex, delta) => {
      const lastVisibleColumnIndex = getters.lastVisibleColumnIndex;
      const firstVisibleColumnIndex = getters.firstVisibleColumnIndex;

      if (
        (delta === 1 && columnIndex >= lastVisibleColumnIndex) ||
        (delta === -1 && columnIndex <= firstVisibleColumnIndex)
      ) {
        return delta === 1 ? lastVisibleColumnIndex : firstVisibleColumnIndex;
      }
      const nextColumnIndex =
        getters.hasData(rowIndex, columnIndex) && getters.hasData(rowIndex, columnIndex + delta)
          ? getters.prevColumnIndexWithData(columnIndex + delta, rowIndex, delta)
          : getters.nextColumnIndexWithData(columnIndex + delta, rowIndex, delta);

      return nextColumnIndex;
    },
    nextRowIndexWithData: (state, getters, rootState, rootGetters) => (columnIndex, rowIndex, delta) => {
      let nextRowIndex = rowIndex;

      while (
        nextRowIndex !== getters.firstVisibleRowIndex &&
        nextRowIndex < getters.lastVisibleRowIndex &&
        !getters.hasData(nextRowIndex, columnIndex)
      ) {
        nextRowIndex += delta;
      }
      return nextRowIndex;
    },
    prevRowIndexWithData: (state, getters, rootState, rootGetters) => (columnIndex, rowIndex, delta) => {
      let nextRowIndex = rowIndex;
      while (
        nextRowIndex !== 0 &&
        nextRowIndex < getters.lastVisibleRowIndex &&
        getters.hasData(nextRowIndex + delta, columnIndex)
      ) {
        nextRowIndex += delta;
      }

      return nextRowIndex < getters.firstVisibleRowIndex ? getters.firstVisibleRowIndex : nextRowIndex;
    },

    nextColumnIndexWithData: (state, getters, rootState, rootGetters) => (columnIndex, rowIndex, delta) => {
      let nextColumnIndex = columnIndex;
      while (
        nextColumnIndex !== getters.firstVisibleColumnIndex &&
        nextColumnIndex < getters.lastVisibleColumnIndex &&
        !getters.hasData(rowIndex, nextColumnIndex)
      ) {
        nextColumnIndex += delta;
      }
      return nextColumnIndex;
    },

    prevColumnIndexWithData: (state, getters, rootState, rootGetters) => (columnIndex, rowIndex, delta) => {
      let nextColumnIndex = columnIndex;
      while (
        nextColumnIndex !== 0 &&
        nextColumnIndex < getters.lastVisibleColumnIndex &&
        getters.hasData(rowIndex, nextColumnIndex + delta)
      ) {
        nextColumnIndex += delta;
      }

      return nextColumnIndex < getters.firstVisibleColumnIndex ? getters.firstVisibleColumnIndex : nextColumnIndex;
    },
    getNextHorizontalCell: (state, getters, rootState) => (mainIndex, delta) => {
      let index = mainIndex + delta;
      let columns = rootState.cells.current?.design.columns || [];

      while (index < columns.length && index >= 0 && !getters.getColumnVisibility(columns[index]?.id)) {
        index += delta;
      }

      if (index < 0 || index >= columns.length) return mainIndex;

      return index;
    },

    getNextVerticalCell: (state, getters, rootState, rootGetters) => (mainIndex, delta) => {
      let index = mainIndex + delta;
      let rows = rootState.cells.current?.design.rows || [];

      while (index < rows.length && index >= 0 && !rootGetters["cells/getRowVisibilityByIndex"](index)) {
        index += delta;
      }

      if (index < 0 || index >= rows.length) return mainIndex;

      return index;
    },
    lastVisibleRowIndex: (state, getters, rootState, rootGetters) => {
      const rows = rootState.cells.current?.design.rows || [];
      for (let i = rows.length - 1; i >= 0; i--) {
        if (rootGetters["cells/getRowVisibilityByIndex"](i)) return i;
      }
      return -1;
    },

    firstVisibleRowIndex: (state, getters, rootState, rootGetters) => {
      const rows = rootState.cells.current?.design.rows || [];
      return rows.findIndex((item, index) => rootGetters["cells/getRowVisibilityByIndex"](index));
    },
    lastVisibleColumnIndex: (state, getters, rootState) => {
      const columns = rootState.cells.current?.design.columns || [];
      for (let i = columns.length - 1; i >= 0; i--) {
        if (getters.getColumnVisibility(columns[i].id)) return i;
      }
      return -1;
    },

    firstVisibleColumnIndex: (state, getters, rootState) => {
      const columns = rootState.cells.current?.design.columns || [];
      return columns.findIndex((item) => getters.getColumnVisibility(item.id));
    },
    getColumnVisibility: (state, getters, rootState) => (columnId) => {
      if (rootState.views.activeCorner === ActiveCorners.NavigationCorner) return true;

      return !(rootState.views.activeView?.columns?.[columnId]?.show === "0");
    },
    hasData: (state, getters, rootState, rootGetters) => (rowIndex, columnIndex) => {
      return ![null, undefined, ""].includes(rootGetters["cells/cellValue"](rowIndex, columnIndex));
    },
  },
};
